import { detectBrand } from '../detectBrand';
import type { CardInput, IProvider } from '../types';

const VINDI_API = 'https://app.vindi.com.br/api/v1/public';
const VINDI_SANDBOX_API = 'https://sandbox-app.vindi.com.br/api/v1/public';

// Nosso brand (detectBrand) → `payment_company_code` da Vindi. Os codes vêm do
// `payment_companies` de GET /payment_methods (validados 2026-08-05): amex e
// diners usam os nomes LONGOS — mandar 'amex'/'diners' devolve 422
// "bandeira/banco não suportado" e derruba a tokenização da bandeira inteira.
// Bandeiras fora do mapa (jcb, discover) são omitidas — a Vindi detecta pelo
// número do cartão.
const VINDI_COMPANY_CODES: Record<string, string> = {
  visa: 'visa',
  mastercard: 'mastercard',
  elo: 'elo',
  amex: 'american_express',
  hipercard: 'hipercard',
  diners: 'diners_club',
};

export class VindiProvider implements IProvider {
  private readonly apiUrl: string;

  constructor(
    private readonly publicKey: string,
    private readonly gatewayConfigId: string,
    environment?: string,
  ) {
    this.apiUrl = environment === 'sandbox' ? VINDI_SANDBOX_API : VINDI_API;
  }

  async generate(card: CardInput): Promise<string> {
    const [month, year] = card.expiration.split('/');
    const companyCode = VINDI_COMPANY_CODES[detectBrand(card.number)];

    const body: Record<string, string> = {
      holder_name: card.holder,
      card_number: card.number.replace(/\s/g, ''),
      card_cvv: card.cvv,
      // Vindi espera a validade como 'MM/YYYY' (doc oficial: "12/2028") — o
      // mesmo formato que o proxy do PCI Vault manda pro mesmo endpoint.
      card_expiration: `${month.padStart(2, '0')}/${year}`,
      payment_method_code: 'credit_card',
    };
    if (companyCode) {
      body.payment_company_code = companyCode;
    }

    // Endpoint público da Vindi (feito pra rodar no browser, escopo PCI
    // reduzido): auth Basic com a chave pública como usuário e senha vazia.
    const response = await fetch(`${this.apiUrl}/payment_profiles`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Basic ${btoa(`${this.publicKey}:`)}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`Vindi tokenize failed: ${error}`);
    }

    // O gateway_token expira em ~5 minutos — o backend troca por um
    // payment_profile permanente na hora da cobrança, não é problema do SDK.
    const data = (await response.json()) as { payment_profile: { gateway_token: string } };
    return data.payment_profile.gateway_token;
  }
}
